import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError, DownloadRequest } from '../../common/api'
import { warehousingItemSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import * as unitsActionCreators from '../units/actionCreators'
import * as currenciesActionCreators from '../currencies/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import * as actionCreators from './actionCreators'
import * as actionTypes from './actionTypes'
import { WarehousingItem } from "../warehousing_items/model";
import { Unit } from "../units/model";
import { Currency } from "../currencies/model";

// Fetch

const fetchWarehousingItemsEpic = (action$, store) => {
    const tableIdentifier = 'warehousing_items_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `warehousing-items/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(warehousingItemSchema)
                        )

                        const items = normalizedEntitiesToRecordMap(normalizedData.entities.warehousing_items, WarehousingItem, normalizedData.result)
                        const units = normalizedEntitiesToRecordMap(normalizedData.entities.units, Unit)
                        const currencies = normalizedEntitiesToRecordMap(normalizedData.entities.currencies, Currency)

                        const observables = [
                            Observable.of(actionCreators.fetchWarehousingItemsFulfilled(items)),
                            Observable.of(unitsActionCreators.fetchUnitsFulfilled(units)),
                            Observable.of(currenciesActionCreators.fetchCurrenciesFulfilled(currencies)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchWarehousingItemsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchWarehousingItemEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().warehousingItems.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `warehousing-items/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, warehousingItemSchema)

                        const warehousingItems = normalizedEntitiesToRecordMap(normalizedData.entities.warehousing_items, WarehousingItem)
                        const units = normalizedEntitiesToRecordMap(normalizedData.entities.units, Unit)
                        const currencies = normalizedEntitiesToRecordMap(normalizedData.entities.currencies, Currency)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchWarehousingItemsFulfilled(warehousingItems)),
                            Observable.of(actionCreators.fetchWarehousingItemFulfilled()),
                            Observable.of(unitsActionCreators.fetchUnitsFulfilled(units)),
                            Observable.of(currenciesActionCreators.fetchCurrenciesFulfilled(currencies))
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchWarehousingItemRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveWarehousingItemEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().warehousingItems.getIn(['current', 'saving']))
        .switchMap(action => {
            const values = { ...action.payload }

            let path = `warehousing-items`
            let method = 'POST'

            if (values.id) {
                path = `warehousing-items/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, warehousingItemSchema)

                        const warehousingItems = normalizedEntitiesToRecordMap(normalizedData.entities.warehousing_items, WarehousingItem)
                        const warehousingItem = warehousingItems.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveWarehousingItemFulfilled(warehousingItem)))
                    })
                    .catch(error => Observable.of(actionCreators.saveWarehousingItemRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteWarehousingItemEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().warehousingItems.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `warehousing-items/${action.payload}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteWarehousingItemFulfilled(action.payload)))
                    .catch(error => Observable.of(actionCreators.deleteWarehousingItemRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Export

const exportWarehousingItemsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT).switchMap(action => {
        const filters = JSON.stringify(action.payload.filters)
        const sorting = JSON.stringify(action.payload.sorting)
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `warehousing-items/export?filters=${filters}&sorting=${sorting}&token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_FULFILLED,
            })
        )
    })

// Cost centers

const fetchWarehousingItemReceiptsEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_RECEIPTS)
        .filter(() => !store.getState().warehousingItems.getIn(['receipts', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_RECEIPTS_STARTED,
                }),
                request({
                    path: `warehousing-items/${action.payload.id}/receipts${action.payload.page ? `?page=${action.payload.page}` : ''}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => Observable.of(actionCreators.fetchWarehousingItemReceiptsFulfilled(ajaxResponse.response)))
                    .catch(error => Observable.of(actionCreators.fetchWarehousingItemReceiptsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_RECEIPTS_CANCELLED))
            )
        )

const exportWarehousingItemReceiptsEpic = (action$, store) =>
    action$.ofType(actionTypes.EXPORT_RECEIPTS).switchMap(action => {
        const token = store.getState().auth.get('accessToken')

        new DownloadRequest({
            url: `warehousing-items/${action.payload}/receipts/export?token=${token}`,
        }).run()

        return Observable.concat(
            Observable.of({
                type: actionTypes.EXPORT_RECEIPTS_FULFILLED,
            })
        )
    })

export default [
    fetchWarehousingItemsEpic,
    fetchWarehousingItemEpic,
    saveWarehousingItemEpic,
    deleteWarehousingItemEpic,
    exportWarehousingItemsEpic,
    fetchWarehousingItemReceiptsEpic,
    exportWarehousingItemReceiptsEpic
]
